import { masterSupabase } from '../lib/supabase-master.js';

/**
 * MaintenanceService - Real Supabase Schema Integration
 * 
 * Based on actual maintenance table structure:
 * - id (UUID)
 * - created_at, updated_at (timestamps)
 * - property_id, unit_id (relations)
 * - address (redundant for performance)
 * - maintenance_type (category)
 * - title, description
 * - priority (hoch, mittel, niedrig)
 * - status (geplant, in_bearbeitung, abgeschlossen)
 * - scheduled_date, completed_date
 * - estimated_cost, actual_cost
 * - contractor_property_name, contractor_contact
 */
class MaintenanceService {
  constructor() {
    this.currentUser = null;
  }

  // Get current authenticated user
  async getCurrentUser() {
    try {
      const { data: { user }, error } = await masterSupabase.auth.getUser();
      if (error || !user) {
        throw new Error('Benutzer nicht authentifiziert');
      }
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('❌ Fehler bei User-Ermittlung:', error);
      throw error;
    }
  }

  // Get user's owner entities
  async getUserOwnerEntities() {
    try {
      const user = await this.getCurrentUser();
      
      const { data: userOwners, error } = await masterSupabase
        .from('owners')
        .select('id, property_name, created_by')
        .eq('created_by', user.id);
      
      if (error) {
        console.error('❌ SUPABASE ERROR:', error);
        throw error;
      }
      
      return userOwners || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Owner Entities:', error);
      return [];
    }
  }

  // ✅ NEW METHOD: Get properties for owner (FIXES THE MODAL ERROR)
  async getPropertiesForOwner(ownerId) {
    try {
      console.log('🔍 Loading properties for owner:', ownerId);
      
      if (!ownerId || ownerId === 'all') {
        // Return all properties if no specific owner
        const { data, error } = await masterSupabase
          .from('properties')
          .select(`
            id,
            property_name,
            property_address,
            owner_entity,
            created_at
          `)
          .order('property_name', { ascending: true });

        if (error) {
          console.error('❌ Error loading all properties:', error);
          throw error;
        }

        console.log('✅ All properties loaded:', data?.length || 0);
        return data || [];
      }

      // Get properties for specific owner
      const { data, error } = await masterSupabase
        .from('properties')
        .select(`
          id,
          property_name,
          property_address,
          owner_entity,
          created_at
        `)
        .eq('owner_entity', ownerId)
        .order('property_name', { ascending: true });

      if (error) {
        console.error('❌ Error loading properties for owner:', error);
        throw error;
      }

      console.log('✅ Properties loaded for owner:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Properties für Owner:', error);
      return [];
    }
  }

  // ✅ NEW METHOD: Get units for property (NEEDED FOR MODAL)
  async getUnitsForProperty(propertyId) {
    try {
      console.log('🔍 Loading units for property:', propertyId);
      
      if (!propertyId) {
        return [];
      }

      const { data, error } = await masterSupabase
        .from('units')
        .select(`
          id,
          unit_identifier,
          unit_type,
          property_id,
          created_at
        `)
        .eq('property_id', propertyId)
        .order('unit_identifier', { ascending: true });

      if (error) {
        console.error('❌ Error loading units for property:', error);
        throw error;
      }

      console.log('✅ Units loaded for property:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Units für Property:', error);
      return [];
    }
  }

  // Get all maintenance tasks
  async getAllMaintenanceTasks() {
    try {
      const { data, error } = await masterSupabase
        .from('maintenance')
        .select(`
          *,
          properties!maintenance_property_id_fkey (
            id,
            property_name,
            owner_entity
          ),
          units!maintenance_unit_id_fkey (
            id,
            unit_identifier,
            property_id
          )
        `)
        .order('scheduled_date', { ascending: true });

      if (error) {
        console.error('❌ Error loading maintenance tasks:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Wartungsaufgaben:', error);
      return [];
    }
  }

  // Get maintenance tasks filtered by owner (through properties)
  async getMaintenanceTasksForOwner(ownerId) {
    try {
      if (!ownerId || ownerId === 'all') {
        return this.getAllMaintenanceTasks();
      }

      // First get properties for this owner
      const { data: properties, error: propError } = await masterSupabase
        .from('properties')
        .select('id')
        .eq('owner_entity', ownerId);

      if (propError) {
        console.error('❌ Error loading properties for owner:', propError);
        throw propError;
      }

      if (!properties || properties.length === 0) {
        return [];
      }

      const propertyIds = properties.map(p => p.id);

      // Get maintenance tasks for these properties
      const { data, error } = await masterSupabase
        .from('maintenance')
        .select(`
          *,
          properties!maintenance_property_id_fkey (
            id,
            property_name,
            owner_entity
          ),
          units!maintenance_unit_id_fkey (
            id,
            unit_identifier,
            property_id
          )
        `)
        .in('property_id', propertyIds)
        .order('scheduled_date', { ascending: true });

      if (error) {
        console.error('❌ Error loading maintenance tasks for owner:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Wartungsaufgaben für Owner:', error);
      return [];
    }
  }

  // Get maintenance tasks for specific property
  async getMaintenanceTasksForProperty(propertyId) {
    try {
      const { data, error } = await masterSupabase
        .from('maintenance')
        .select(`
          *,
          properties!maintenance_property_id_fkey (
            id,
            property_name,
            owner_entity
          ),
          units!maintenance_unit_id_fkey (
            id,
            unit_identifier,
            property_id
          )
        `)
        .eq('property_id', propertyId)
        .order('scheduled_date', { ascending: true });

      if (error) {
        console.error('❌ Error loading maintenance tasks for property:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Wartungsaufgaben für Property:', error);
      return [];
    }
  }

  // Get upcoming maintenance tasks (next 30 days)
  async getUpcomingMaintenanceTasks(ownerId = null) {
    try {
      const currentDate = new Date();
      const thirtyDaysFromNow = new Date(currentDate);
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      if (ownerId && ownerId !== 'all') {
        // Get tasks for specific owner
        const allTasks = await this.getMaintenanceTasksForOwner(ownerId);
        return allTasks.filter(task => {
          const scheduledDate = new Date(task.scheduled_date);
          return scheduledDate >= currentDate && 
                 scheduledDate <= thirtyDaysFromNow &&
                 task.status !== 'abgeschlossen';
        });
      } else {
        // Get all upcoming tasks
        const { data, error } = await masterSupabase
          .from('maintenance')
          .select(`
            *,
            properties!maintenance_property_id_fkey (
              id,
              property_name,
              owner_entity
            ),
            units!maintenance_unit_id_fkey (
              id,
              unit_identifier,
              property_id
            )
          `)
          .gte('scheduled_date', currentDate.toISOString().split('T')[0])
          .lte('scheduled_date', thirtyDaysFromNow.toISOString().split('T')[0])
          .neq('status', 'abgeschlossen')
          .order('scheduled_date', { ascending: true });

        if (error) {
          console.error('❌ Error loading upcoming maintenance tasks:', error);
          throw error;
        }

        return data || [];
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der anstehenden Wartungsaufgaben:', error);
      return [];
    }
  }

  // Get overdue maintenance tasks
  async getOverdueMaintenanceTasks(ownerId = null) {
    try {
      const currentDate = new Date();

      if (ownerId && ownerId !== 'all') {
        // Get tasks for specific owner
        const allTasks = await this.getMaintenanceTasksForOwner(ownerId);
        return allTasks.filter(task => {
          const scheduledDate = new Date(task.scheduled_date);
          return scheduledDate < currentDate && task.status !== 'abgeschlossen';
        });
      } else {
        // Get all overdue tasks
        const { data, error } = await masterSupabase
          .from('maintenance')
          .select(`
            *,
            properties!maintenance_property_id_fkey (
              id,
              property_name,
              owner_entity
            ),
            units!maintenance_unit_id_fkey (
              id,
              unit_identifier,
              property_id
            )
          `)
          .lt('scheduled_date', currentDate.toISOString().split('T')[0])
          .neq('status', 'abgeschlossen')
          .order('scheduled_date', { ascending: true });

        if (error) {
          console.error('❌ Error loading overdue maintenance tasks:', error);
          throw error;
        }

        return data || [];
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der überfälligen Wartungsaufgaben:', error);
      return [];
    }
  }

  // Create new maintenance task
  async createMaintenanceTask(taskData) {
    try {
      const maintenanceTask = {
        property_id: taskData.property_id,
        user_id: (await masterSupabase.auth.getUser()).data.user?.id,
        unit_id: taskData.unit_id || null,
        maintenance_type: taskData.maintenance_type,
        title: taskData.title,
        description: taskData.description || '',
        priority: taskData.priority || 'mittel',
        status: taskData.status || 'geplant',
        scheduled_date: taskData.scheduled_date,
        estimated_cost: taskData.estimated_cost || 0,
        contractor_name: taskData.contractor_name || '',
        contractor_contact: taskData.contractor_contact || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await masterSupabase
        .from('maintenance')
        .insert([maintenanceTask])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating maintenance task:', error);
        throw error;
      }

      console.log('✅ Maintenance task created:', data);
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Erstellen der Wartungsaufgabe:', error);
      throw error;
    }
  }

  // Update maintenance task
  async updateMaintenanceTask(taskId, updateData) {
    try {
      const updates = {
        ...updateData,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await masterSupabase
        .from('maintenance')
        .update(updates)
        .eq('id', taskId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating maintenance task:', error);
        throw error;
      }

      console.log('✅ Maintenance task updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Aktualisieren der Wartungsaufgabe:', error);
      throw error;
    }
  }

  // Mark maintenance task as completed
  async completeMaintenanceTask(taskId, actualCost = null) {
    try {
      const updates = {
        status: 'abgeschlossen',
        completed_date: new Date().toISOString().split('T')[0],
        actual_cost: actualCost,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await masterSupabase
        .from('maintenance')
        .update(updates)
        .eq('id', taskId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error completing maintenance task:', error);
        throw error;
      }

      console.log('✅ Maintenance task completed:', data);
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Abschließen der Wartungsaufgabe:', error);
      throw error;
    }
  }

  // Delete maintenance task
  async deleteMaintenanceTask(taskId) {
    try {
      const { error } = await masterSupabase
        .from('maintenance')
        .delete()
        .eq('id', taskId);

      if (error) {
        console.error('❌ Error deleting maintenance task:', error);
        throw error;
      }

      console.log('✅ Maintenance task deleted');
      return true;
    } catch (error) {
      console.error('❌ Fehler beim Löschen der Wartungsaufgabe:', error);
      throw error;
    }
  }

  // Get maintenance statistics
  async getMaintenanceStatistics(ownerId = null) {
    try {
      const [upcoming, overdue, allTasks] = await Promise.all([
        this.getUpcomingMaintenanceTasks(ownerId),
        this.getOverdueMaintenanceTasks(ownerId),
        ownerId ? this.getMaintenanceTasksForOwner(ownerId) : this.getAllMaintenanceTasks()
      ]);

      const totalCost = upcoming.reduce((sum, task) => sum + (task.estimated_cost || 0), 0);
      const affectedProperties = new Set(allTasks.map(task => task.property_id)).size;

      return {
        upcoming: upcoming.length,
        overdue: overdue.length,
        totalEstimatedCost: totalCost,
        affectedProperties: affectedProperties,
        totalTasks: allTasks.length
      };
    } catch (error) {
      console.error('❌ Fehler beim Laden der Wartungsstatistiken:', error);
      return {
        upcoming: 0,
        overdue: 0,
        totalEstimatedCost: 0,
        affectedProperties: 0,
        totalTasks: 0
      };
    }
  }

  // Helper function to format priority for display
  formatPriority(priority) {
    const priorityMap = {
      'hoch': 'Hoch',
      'mittel': 'Mittel', 
      'niedrig': 'Niedrig'
    };
    return priorityMap[priority] || priority;
  }

  // Helper function to format status for display
  formatStatus(status) {
    const statusMap = {
      'geplant': 'Geplant',
      'in_bearbeitung': 'In Bearbeitung',
      'abgeschlossen': 'Abgeschlossen'
    };
    return statusMap[status] || status;
  }

  // Helper function to get priority color
  getPriorityColor(priority) {
    const colorMap = {
      'hoch': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      'mittel': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      'niedrig': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
    };
    return colorMap[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }

  // Helper function to get status color
  getStatusColor(status) {
    const colorMap = {
      'geplant': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      'in_bearbeitung': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      'abgeschlossen': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }
}

// Export singleton instance
export const maintenanceService = new MaintenanceService();

